import { useCallback, useEffect, useRef } from "react";
import { authNetConfig, isCardPaymentConfigured } from "@/config/paymentConfig";

// AcceptUI (Authorize.Net's vendor-hosted payment lightbox) integration. The card is collected
// entirely inside Authorize.Net's iframe — no PAN/CVV ever touches our app state; we only receive
// the opaque nonce plus vendor-masked display data. Field validation also happens inside the
// hosted form; vendor error text surfaced here covers everything else.
//
// TWO vendor constraints shape this file, both verified against the real v3/AcceptUI.js source:
//
// 1. AcceptUI binds ONCE, to `document.getElementsByClassName("AcceptUI")[0]`, snapshotting that
//    element's data-* attributes at that instant. There is no delegation, no re-scan and no
//    re-init API — so the bound trigger is a STATIC hidden host button in index.html (a
//    React-rendered one would be destroyed on navigating away from checkout, and re-injecting the
//    script to re-bind stacks window "message" listeners so the response handler would fire once
//    per injection). App buttons proxy-click the host via `openLightbox`.
// 2. Init unconditionally BUILDS the lightbox: it loads authorize.net's acceptMain.html iframe,
//    appends two full-viewport divs, registers a resize listener and calls window.scrollTo(0, 0).
//    None of that may happen on marketing or admin pages, so the SCRIPT is injected lazily —
//    exactly once, behind a module-level guard — when a surface that needs it first mounts.
//
// The one piece the vendor resolves lazily is `data-responseHandler`: it looks up
// `window[<name>]` at response time, which lets this hook own the global handler's lifecycle.

// Must match the host button's `data-responseHandler` attribute in index.html. The contract
// between this file and index.html is pinned by __tests__/acceptUiHostContract.test.ts.
export const RESPONSE_HANDLER_NAME = "usnaraAcceptUiResponse";

// Must match the host button's `id` in index.html.
export const ACCEPT_UI_HOST_ID = "acceptui-host";

interface AcceptUiMessage {
    code: string;
    text: string;
}

// Shape of the object AcceptUI.js passes to the response handler.
export interface AcceptUiResponse {
    messages: { resultCode: string; message: AcceptUiMessage[] };
    opaqueData?: { dataDescriptor: string; dataValue: string };
    // Vendor-masked display data — never the real PAN.
    encryptedCardData?: { cardNumber: string; expDate: string; bin: string };
}

// Successful capture routed back to the consumer. `token` is the single-use nonce encoded for
// our API as "<dataDescriptor>|<dataValue>" (see CreateOrderRequest.vendorPaymentToken).
export interface AcceptUiCapture {
    token: string;
    maskedCard: string;
    expDate: string;
}

declare global {
    interface Window {
        usnaraAcceptUiResponse?: (response: AcceptUiResponse) => void;
    }
}

// Shown to shoppers. Anything a developer needs (env var names, script/host state) goes to
// console.error instead — a payment surface must not display internal configuration detail.
const GENERIC_UNAVAILABLE =
    "We couldn't load the secure payment form. Please refresh and try again.";
const FALLBACK_ERROR =
    "Your card could not be verified. Please check the details and try again.";

// Marks the element we injected, so the guard below can be checked against the document.
const SCRIPT_MARKER = "data-acceptui";

// Exactly one injection per document, no matter how many consumers mount or how often — a second
// injection would re-run the vendor's whole IIFE and stack another window "message" listener, so
// every lightbox response would invoke the response handler once per injection. This is also the
// correct mechanism under StrictMode, whose mount→unmount→mount double-invoke would otherwise
// inject twice. The injected element is the source of truth (the promise alone could outlive it).
let scriptReady: Promise<void> | null = null;

const loadAcceptUi = (): Promise<void> => {
    if (scriptReady && document.querySelector(`script[${SCRIPT_MARKER}]`)) {
        return scriptReady;
    }

    scriptReady = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = authNetConfig.acceptUiUrl;
        script.charset = "utf-8";
        script.setAttribute(SCRIPT_MARKER, "");

        script.addEventListener("load", () => {
            // Readiness is NOT `window.AcceptUI`: the script's tail is
            // `}(window.AcceptUI = window.AcceptUI || {})`, so that global is truthy from the
            // first statement — long before anything is bound. The real signal is the binding
            // itself, which happens at readyState "complete": synchronously inside script
            // execution when the document is already complete, otherwise deferred by the script's
            // own `document.onreadystatechange`. window "load" fires after that readystatechange,
            // so it is the earliest point the host button is guaranteed bound. (A hard load
            // landing straight on /c/shopping/checkout hits the deferred case.)
            if (document.readyState === "complete") {
                resolve();
                return;
            }
            window.addEventListener("load", () => resolve(), { once: true });
        });

        script.addEventListener("error", () => {
            console.error(
                `AcceptUI failed to load from "${authNetConfig.acceptUiUrl}" (VITE_AUTHNET_ACCEPTUI_URL). ` +
                    "Card capture is unavailable — check the URL, the network, and that the page is served over HTTPS.",
            );
            // Clear the guard so a later attempt can retry the download. Drop the dead element
            // with it: the guard's invariant is that the injected element IS the state, and a
            // stale element left in <head> would still match the query above while the promise
            // has already been discarded.
            scriptReady = null;
            script.remove();
            reject(new Error("AcceptUI script failed to load."));
        });

        document.head.appendChild(script);
    });

    return scriptReady;
};

interface UseAcceptUiOptions {
    onCapture: (capture: AcceptUiCapture) => void;
    onError: (message: string) => void;
}

export const useAcceptUi = ({ onCapture, onError }: UseAcceptUiOptions) => {
    const isConfigured = isCardPaymentConfigured();

    // Latest-callback refs so the global handler is registered once per mount, not re-registered
    // every render (consumers pass inline callbacks).
    const onCaptureRef = useRef(onCapture);
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onCaptureRef.current = onCapture;
        onErrorRef.current = onError;
    });

    useEffect(() => {
        // Start the download as soon as a payment surface mounts so the lightbox is ready by the
        // time the user clicks. Without keys the lightbox cannot work at all, so an unconfigured
        // environment never contacts the vendor. A failure here is surfaced at open time.
        if (!isConfigured) return;
        void loadAcceptUi().catch(() => {
            /* surfaced by openLightbox */
        });
    }, [isConfigured]);

    useEffect(() => {
        const handler = (response: AcceptUiResponse) => {
            if (response.messages?.resultCode === "Ok") {
                if (response.opaqueData) {
                    onCaptureRef.current({
                        token: `${response.opaqueData.dataDescriptor}|${response.opaqueData.dataValue}`,
                        maskedCard: response.encryptedCardData?.cardNumber ?? "",
                        expDate: response.encryptedCardData?.expDate ?? "",
                    });
                    return;
                }
                // Ok with no nonce: the vendor's message text is a SUCCESS string ("Successful."),
                // which must never be shown to a shopper as the reason their payment failed.
                console.error(
                    "AcceptUI returned resultCode Ok without opaqueData.",
                    response.messages,
                );
                onErrorRef.current(FALLBACK_ERROR);
                return;
            }
            onErrorRef.current(
                response.messages?.message?.[0]?.text ?? FALLBACK_ERROR,
            );
        };

        window[RESPONSE_HANDLER_NAME] = handler;
        return () => {
            // Only retract OUR registration — a second consumer may have replaced it — and leave a
            // no-op behind so a response arriving after unmount (lightbox still open) can't throw
            // inside the vendor's own message listener, where nothing could catch it.
            if (window[RESPONSE_HANDLER_NAME] === handler) {
                window[RESPONSE_HANDLER_NAME] = () => {};
            }
        };
    }, []);

    const openLightbox = useCallback(() => {
        // Awaits the injection promise, so a click landing before the download finishes still
        // opens the lightbox instead of silently no-oping.
        void loadAcceptUi()
            .then(() => {
                const host = document.getElementById(ACCEPT_UI_HOST_ID);
                if (!host) {
                    console.error(
                        `AcceptUI host button #${ACCEPT_UI_HOST_ID} is missing from index.html — ` +
                            "the lightbox cannot be opened.",
                    );
                    onErrorRef.current(GENERIC_UNAVAILABLE);
                    return;
                }
                host.click();
            })
            .catch(() => onErrorRef.current(GENERIC_UNAVAILABLE));
    }, []);

    return { isConfigured, openLightbox };
};
